import React, { useState, useEffect, useCallback } from 'react';
import { useTheme } from '../../../Context/ThemeContext';
import { BotQueueHandler, BotQueueItem, QueueSummary, QueueResponse } from './BotQueueHandler';

const getStatusColor = (status: string | undefined): string => {
    switch (status) {
        case 'Processing': return '#3b82f6';
        case 'Completed': return '#22c55e';
        case 'Failed': return '#ef4444';
        case 'Pending': return '#f59e0b';
        default: return '#6b7280';
    }
};

const formatDate = (dateStr: string | undefined): string => {
    if (!dateStr) return '-';
    const date = new Date(dateStr);
    return date.toLocaleString(undefined, {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const BotQueuePage: React.FC = () => {
    const { theme, isMobile } = useTheme();

    const [items, setItems] = useState<BotQueueItem[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [currentPage, setCurrentPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [summary, setSummary] = useState<QueueSummary | null>(null);
    const [summaryLoading, setSummaryLoading] = useState(true);
    const [showProcessedOnly, setShowProcessedOnly] = useState(false);
    const pageSize = 15;

    const fetchSummary = useCallback(async () => {
        setSummaryLoading(true);
        try {
            const handler = new BotQueueHandler();
            const response = await handler.getBotQueueSummary();
            setSummary(response);
        } catch (err) {
            console.error('Error fetching bot queue summary:', err);
        } finally {
            setSummaryLoading(false);
        }
    }, []);

    const fetchItems = useCallback(async (page: number) => {
        setLoading(true);
        setError(null);
        try {
            const handler = new BotQueueHandler();
            const response: QueueResponse = await handler.getBotQueueItems(page, pageSize, showProcessedOnly || undefined);
            setItems(response.items || []);
            setTotalCount(response.count || 0);
        } catch (err) {
            console.error('Error fetching bot queue items:', err);
            setError('Failed to load bot queue data.');
            setItems([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, [showProcessedOnly]);

    useEffect(() => {
        fetchSummary();
        fetchItems(currentPage);
    }, [fetchSummary, fetchItems, currentPage, showProcessedOnly]);

    const totalPages = Math.ceil(totalCount / pageSize);

    const statCards = [
        {
            label: 'Total',
            value: summary?.totalInQueue ?? '-',
            color: '#3b82f6',
            bgColor: 'rgba(59, 130, 246, 0.15)',
        },
        {
            label: 'Processing',
            value: summary?.currentlyProcessing ?? '-',
            color: '#f59e0b',
            bgColor: 'rgba(245, 158, 11, 0.15)',
        },
        {
            label: 'Failed',
            value: summary?.failedItems ?? '-',
            color: '#ef4444',
            bgColor: 'rgba(239, 68, 68, 0.15)',
        },
    ];

    const styles: Record<string, React.CSSProperties> = {
        container: {
            padding: 0,
        },
        title: {
            fontSize: isMobile ? '22px' : '28px',
            fontWeight: 700,
            color: theme.titleColor,
            marginBottom: '8px',
        },
        subtitle: {
            fontSize: '14px',
            color: theme.textSecondary,
            marginBottom: '24px',
        },
        statsGrid: {
            display: 'grid',
            gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
            gap: '16px',
            marginBottom: '24px',
        },
        statCard: {
            background: theme.card,
            borderRadius: '10px',
            padding: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
            display: 'flex',
            alignItems: 'center',
            gap: '16px',
        },
        statIconCircle: {
            width: '48px',
            height: '48px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '20px',
            fontWeight: 700,
            flexShrink: 0,
        },
        statInfo: {
            display: 'flex',
            flexDirection: 'column' as const,
        },
        statLabel: {
            fontSize: '13px',
            color: theme.textSecondary,
            fontWeight: 500,
        },
        statValue: {
            fontSize: '24px',
            fontWeight: 700,
            color: theme.text,
        },
        tableContainer: {
            background: theme.card,
            borderRadius: '10px',
            overflow: 'hidden',
            boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        },
        tableWrapper: {
            overflowX: 'auto' as const,
        },
        table: {
            width: '100%',
            borderCollapse: 'collapse' as const,
            tableLayout: 'fixed' as const,
            minWidth: isMobile ? '700px' : 'auto',
        },
        th: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontWeight: 600,
            fontSize: '13px',
            color: theme.textSecondary,
            background: theme.tableHeader,
            borderBottom: `1px solid ${theme.border}`,
            userSelect: 'none' as const,
            whiteSpace: 'nowrap' as const,
        },
        td: {
            padding: '14px 16px',
            textAlign: 'center' as const,
            fontSize: '14px',
            color: theme.text,
            borderBottom: `1px solid ${theme.border}`,
            whiteSpace: 'nowrap' as const,
        },
        badge: {
            display: 'inline-block',
            padding: '4px 10px',
            borderRadius: '12px',
            fontSize: '12px',
            fontWeight: 600,
        },
        pagination: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            padding: '16px',
            borderTop: `1px solid ${theme.border}`,
            flexWrap: 'wrap' as const,
            gap: '12px',
        },
        paginationInfo: {
            fontSize: '14px',
            color: theme.textSecondary,
        },
        paginationButtons: {
            display: 'flex',
            gap: '8px',
            alignItems: 'center',
        },
        pageButton: {
            padding: '8px 14px',
            fontSize: '13px',
            fontWeight: 500,
            border: `1px solid ${theme.inputBorder}`,
            borderRadius: '6px',
            background: theme.inputBg,
            color: theme.text,
            cursor: 'pointer',
        },
        pageButtonDisabled: {
            opacity: 0.5,
            cursor: 'not-allowed',
        },
        spinner: {
            width: '24px',
            height: '24px',
            border: '2px solid rgba(59, 130, 246, 0.3)',
            borderTop: '2px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
        },
        errorBanner: {
            padding: '12px 16px',
            borderRadius: '8px',
            background: 'rgba(239, 68, 68, 0.15)',
            border: '1px solid rgba(239, 68, 68, 0.3)',
            color: '#ef4444',
            fontSize: '14px',
            fontWeight: 500,
            marginBottom: '16px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
        },
        emptyRow: {
            textAlign: 'center' as const,
            padding: '40px',
            color: theme.textSecondary,
            fontStyle: 'italic' as const,
        },
    };

    return (
        <div style={styles.container}>
            <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>

            <h1 style={styles.title}>Bot Queue</h1>
            <p style={styles.subtitle}>Monitor bot game processing queue</p>

            {error && (
                <div style={styles.errorBanner}>
                    <span>{error}</span>
                    <button
                        onClick={() => { fetchSummary(); fetchItems(currentPage); }}
                        style={{ padding: '4px 12px', fontSize: '13px', fontWeight: 600, background: '#ef4444', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer' }}
                    >
                        Retry
                    </button>
                </div>
            )}

            {/* Summary Stat Cards */}
            <div style={styles.statsGrid}>
                {statCards.map((card) => (
                    <div key={card.label} style={styles.statCard}>
                        <div style={{
                            ...styles.statIconCircle,
                            background: card.bgColor,
                            color: card.color,
                        }}>
                            {summaryLoading ? (
                                <div style={{ ...styles.spinner, width: '20px', height: '20px', borderWidth: '2px' }} />
                            ) : (
                                <span style={{ fontSize: '16px' }}>#</span>
                            )}
                        </div>
                        <div style={styles.statInfo}>
                            <span style={styles.statLabel}>{card.label}</span>
                            <span style={styles.statValue}>
                                {summaryLoading ? '...' : (typeof card.value === 'number' ? card.value.toLocaleString() : card.value)}
                            </span>
                        </div>
                    </div>
                ))}
            </div>

            {/* Filter */}
            <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '16px' }}>
                <input
                    type="checkbox"
                    id="showProcessedOnly"
                    checked={showProcessedOnly}
                    onChange={(e) => { setShowProcessedOnly(e.target.checked); setCurrentPage(1); }}
                    style={{ width: '16px', height: '16px', cursor: 'pointer', accentColor: '#3b82f6' }}
                />
                <label htmlFor="showProcessedOnly" style={{ fontSize: '14px', fontWeight: 500, color: theme.text, cursor: 'pointer' }}>
                    Show Processed Only
                </label>
            </div>

            {/* Queue Items Table */}
            <div style={styles.tableContainer}>
                <div style={styles.tableWrapper}>
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Game ID</th>
                                <th style={styles.th}>Status</th>
                                <th style={styles.th}>Difficulty</th>
                                <th style={styles.th}>Created</th>
                                <th style={styles.th}>Processed</th>
                                <th style={styles.th}>Retries</th>
                                <th style={styles.th}>Error</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={7} style={styles.td}>
                                        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
                                            <div style={styles.spinner} />
                                        </div>
                                    </td>
                                </tr>
                            ) : items.length === 0 ? (
                                <tr>
                                    <td colSpan={7} style={{ ...styles.td, ...styles.emptyRow }}>
                                        No bot queue items found
                                    </td>
                                </tr>
                            ) : (
                                items.map((item) => {
                                    const statusLabel = item.processedAt ? 'Completed' : item.isProcessing ? 'Processing' : (item.errors ?? 0) > 0 ? 'Failed' : 'Pending';
                                    const statusColor = getStatusColor(statusLabel);
                                    return (
                                        <tr
                                            key={item.id}
                                            onMouseEnter={(e) => { e.currentTarget.style.background = theme.tableRowHover; }}
                                            onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
                                        >
                                            <td style={{ ...styles.td, fontSize: '12px', fontFamily: 'monospace' }}>
                                                {item.gameId ? item.gameId.substring(0, 12) + '...' : '-'}
                                            </td>
                                            <td style={styles.td}>
                                                <span style={{
                                                    ...styles.badge,
                                                    background: `${statusColor}22`,
                                                    color: statusColor,
                                                }}>
                                                    {statusLabel}
                                                </span>
                                            </td>
                                            <td style={styles.td}>{item.score ?? '-'}</td>
                                            <td style={styles.td}>{formatDate(item.creationDate)}</td>
                                            <td style={styles.td}>{formatDate(item.processedAt)}</td>
                                            <td style={styles.td}>
                                                {item.errors ?? 0}
                                            </td>
                                            <td style={{ ...styles.td, maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', color: item.lastError ? '#ef4444' : theme.textSecondary, fontSize: '12px' }}>
                                                {item.lastError || '-'}
                                            </td>
                                        </tr>
                                    );
                                })
                            )}
                        </tbody>
                    </table>
                </div>

                {/* Pagination */}
                {totalCount > 0 && (
                    <div style={styles.pagination}>
                        <span style={styles.paginationInfo}>
                            Showing {Math.min((currentPage - 1) * pageSize + 1, totalCount)} - {Math.min(currentPage * pageSize, totalCount)} of {totalCount.toLocaleString()} items
                        </span>
                        <div style={styles.paginationButtons}>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage(1)}
                                disabled={currentPage === 1}
                            >
                                First
                            </button>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage === 1 ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                                disabled={currentPage === 1}
                            >
                                Previous
                            </button>
                            <span style={{ padding: '8px 14px', fontSize: '13px', color: theme.text }}>
                                Page {currentPage} of {totalPages || 1}
                            </span>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                                disabled={currentPage >= totalPages}
                            >
                                Next
                            </button>
                            <button
                                style={{ ...styles.pageButton, ...(currentPage >= totalPages ? styles.pageButtonDisabled : {}) }}
                                onClick={() => setCurrentPage(totalPages)}
                                disabled={currentPage >= totalPages}
                            >
                                Last
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default BotQueuePage;
